<template>
  <router-link
    v-if="to && to !== ''"
    :to="!to.includes('www') && !to.includes('/media') ? to : ''"
    v-bind="$attrs"
    :disabled="isDisabled"
    :class="[
      'flex w-fit items-center justify-center rounded-md text-center text-sm font-semibold leading-6',
      isDisabled ? getClassForType.disabled : getClassForType.classes,
      getSize()
    ]"
    :target="newWindow === true ? '_blank' : ''"
    type="submit"
    @click="handleExternLink(to, newWindow)"
  >
    <span v-if="screenReaderText != undefined" class="sr-only">{{ screenReaderText }}</span>
    <SpinnerComponent v-if="isLoading === true" variant="dark" size="small" />
    <div
      :class="[
        'flex items-center',
        {
          'flex-row-reverse': iconDirection === 'right'
        }
      ]"
    >
      <font-awesome-icon
        v-if="icon != undefined && isLoading === false"
        :size="iconSize"
        class="fatshrink-0 w-5"
        :class="[iconClass, iconDirection === 'right' ? 'ml-2' : 'mr-2']"
        aria-hidden="true"
        :icon="icon"
        :spin="iconSpin"
      />
      <span :class="textClass" class="line-clamp-1">{{ text }}</span>
      <slot></slot>
    </div>
  </router-link>
  <button
    v-else
    type="submit"
    :class="[
      'flex w-fit items-center justify-center rounded-md text-center text-sm font-semibold',
      isDisabled ? getClassForType.disabled : getClassForType.classes,
      getSize()
    ]"
    :disabled="isDisabled"
    v-bind="$attrs"
    @click="buttonClicked($event)"
  >
    <span v-if="screenReaderText != undefined" class="sr-only">{{ screenReaderText }}</span>
    <SpinnerComponent v-if="isLoading" variant="dark" size="small" />
    <div
      :class="[
        'flex items-center',
        {
          'flex-row-reverse': iconDirection === 'right'
        }
      ]"
    >
      <font-awesome-icon
        v-if="icon != undefined && isLoading === false"
        :size="iconSize"
        class="-ml-0.5 h-5 w-5"
        :class="[iconClass, iconDirection === 'right' ? 'ml-2' : text !== '' ? 'mr-2' : 'mr-0']"
        aria-hidden="true"
        :icon="icon"
        :spin="iconSpin"
      />
      <span :class="textClass" class="line-clamp-1">{{ text }}</span>
      <slot></slot>
    </div>
  </button>
</template>
<script setup lang="ts">
import { type IButtonComponent } from '@/interfaces/components/IButtonComponent'
import { handleExternLink } from '@/application/helpers/urlHelper'
import { ButtonType } from '@/interfaces/enums'

const emit = defineEmits(['clicked'])

const {
  text = '',
  isDisabled = false,
  isLoading = false,
  type = ButtonType.Solid, // Set the default type to Solid
  size = 'lg',
  iconSize = 'lg',
  iconDirection = '',
  iconClass = '',
  to = '',
  preventDefaultStopPropagation = false,
  iconSpin = false,
  color = 'brand',
  newWindow = false
} = defineProps<IButtonComponent>()

const getSize = () => {
  if (size === 'lg') {
    return 'px-8 py-2.5'
  } else if (
    (size === 'sm' && type === ButtonType.Transparent) ||
    (size === 'sm' && type === ButtonType.Link)
  ) {
    return 'px-0 py-0'
  }

  return 'px-5 py-2'
}

const buttonClicked = (event: Event) => {
  if (preventDefaultStopPropagation === true) {
    event.preventDefault()
    event.stopPropagation()
  }

  emit('clicked')
}

const getClassForType = computed((): { classes: string; disabled: string } => {
  switch (type) {
    case ButtonType.Transparent: {
      return {
        classes: 'bg-none border-0 shadow-none w-0 hover:text-gray-500',
        disabled: ''
      }
    }
    case ButtonType.Soft:
      return {
        classes:
          'rounded-md bg-gray-100 text-sm font-semibold text-gray-600 shadow-sm hover:bg-gray-200 cursor-pointer',
        disabled:
          'rounded-md bg-gray-50 text-xs font-semibold text-gray-600 shadow-sm hover:bg-gray-100 cursor-not-allowed'
      }
    case ButtonType.Outline:
      return {
        classes: `rounded-md bg-none text-sm font-semibold text-${color}-900 shadow-sm ring-1 ring-inset ring-${color}-300 hover:bg-${color}-100 cursor-pointer`,
        disabled:
          'text-gray-500 border-1 border-gray-300 cursor-not-allowed shadow-sm shadow-sm ring-1 ring-inset ring-gray-300'
      }
    case ButtonType.Secondary:
      return {
        classes: `rounded-md bg-emerald-600 text-sm font-semibold text-white shadow-sm hover:bg-emerald-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-800 cursor-pointer`,
        disabled: 'text-gray-600 bg-gray-300 border-0 cursor-not-allowed shadow-sm'
      }
    case ButtonType.Link: {
      return {
        classes:
          'bg-none border-0 shadow-none w-0 text-blue-600 hover:text-blue-400 cursor-pointer',
        disabled: 'text-blue-300 bg-none cursor-not-allowed'
      }
    }
    default:
      return {
        classes: `rounded-md bg-${color}-800 text-sm font-semibold text-white shadow-sm hover:bg-${color}-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-${color}-800 cursor-pointer`,
        disabled: 'text-gray-600 bg-gray-300 border-0 cursor-not-allowed shadow-sm'
      }
  }
})
</script>
